import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import { api } from "../../lib/api";
import {
  Building2, BarChart3, ShieldCheck, Users, ArrowRight, CheckCircle2, ChevronDown,
  FileSignature, Plane, MapPin, UserCheck, RefreshCw, Network, ClipboardList, Banknote,
  HeartPulse, Microscope, Dumbbell, Stethoscope, Crown, Video,
  Gauge, Clock, Award, FileCheck, BookOpenCheck, Send, Scale, Sparkles, ExternalLink,
} from "lucide-react";

const heroImg = "https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg";

const SOLUTIONS = [
  { Icon: Building2, t: "Unified workforce system", d: "Travel, PRN, local, and permanent staffing on a single contract, invoice, and audit trail." },
  { Icon: ShieldCheck, t: "Compliance-first submissions", d: "Facility-specific rule sets enforce licensure, certifications, and orientation before a candidate reaches your scheduler." },
  { Icon: BarChart3, t: "Program-level visibility", d: "Fill rate, average bill, overtime exposure, and margin — instrumented to the cost center and exportable to finance." },
  { Icon: Network, t: "Vendor-neutral orchestration", d: "Multi-supplier VMS for MSP programs, with submission tracking, timekeeping, and consolidated billing." },
];

const STAFFING_MODELS = [
  { Icon: FileSignature, t: "Contract Staffing", d: "Facility-defined contracts from 13 to 26 weeks with dedicated account management and candidate submission controls.", anchor: "contract-staffing" },
  { Icon: Plane, t: "Travel Staffing", d: "13-week and longer assignments nationwide. Pre-cleared credentials, transparent rate structures, rapid deployment.", anchor: "travel-staffing" },
  { Icon: MapPin, t: "Local / Per Diem", d: "Credentialed open-shift marketplace for same-week coverage, governed by your facility's specific rule set.", anchor: "local-per-diem" },
  { Icon: UserCheck, t: "Direct Hire", d: "Permanent clinical placement with sourcing, screening, and offer-stage negotiation — fee-for-hire or retained models.", anchor: "permanent-staffing" },
  { Icon: RefreshCw, t: "Temp-to-Perm", d: "Evaluate clinicians in a contract engagement and convert on pre-agreed terms when performance clears.", anchor: "temp-to-perm" },
  { Icon: Banknote, t: "Payroll Services", d: "Compliant payrolling for W-2 and 1099 clinicians across all 50 states — multi-state tax, benefits and workers' comp handled end-to-end.", anchor: "payroll" },
  { Icon: ClipboardList, t: "SOW / Project-Based", d: "Scoped clinical engagements (EMR go-lives, census surges, interim programs) with fixed-outcome contracts and defined deliverables.", anchor: "sow" },
  { Icon: Network, t: "EOR / AOR", d: "Employer- and agent-of-record services — compliant hiring, onboarding and payrolling under our entity, fronted to your program.", anchor: "eor-aor" },
];

const ROLES = [
  { Icon: HeartPulse, t: "Nursing", d: "RN, LPN, CNA across ICU, ER, Med-Surg, OR, L&D, and specialty units.", chip: "Most specialties" },
  { Icon: Microscope, t: "Allied Health", d: "Lab, imaging, respiratory therapy, cardiology technicians, surgical techs.", chip: "Allied" },
  { Icon: Dumbbell, t: "Therapy", d: "PT, OT, SLP, COTA, PTA — inpatient, outpatient, skilled nursing, home health.", chip: "Therapy" },
  { Icon: Stethoscope, t: "Physicians & APPs", d: "MD, DO, NP, PA across hospitalist, specialty, locum, and permanent roles.", chip: "Physician / APP" },
  { Icon: Crown, t: "Executive Leadership", d: "Interim CNO, CMO, service-line directors, and clinical operations leadership.", chip: "Leadership" },
  { Icon: Video, t: "Telehealth", d: "Virtual-care clinicians for tele-ICU, behavioral health, urgent care, and specialty consult.", chip: "Telehealth" },
];

const WHY = [
  { Icon: Clock, t: "Responsive by design", d: "Typical travel requisitions receive a qualified shortlist within two business days; per diem coverage confirmed in hours." },
  { Icon: Scale, t: "Procurement-friendly", d: "Transparent rate cards, MSA-ready paper, tiered escalation protocols, and finance-grade reporting." },
  { Icon: FileCheck, t: "Credentialing discipline", d: "Primary-source verified licensure and certifications. Facility-specific checklists enforced before submission." },
  { Icon: Gauge, t: "Measured performance", d: "Monthly program reviews with fill rate, cancellation rate, time-to-start, and clinician-tenure metrics." },
  { Icon: BookOpenCheck, t: "Clinical quality", d: "Specialty competency checklists signed by recruiters and hiring facilities. References validated, not summarized." },
  { Icon: Award, t: "Accountable partnership", d: "Single executive sponsor per account; escalation SLAs written into every engagement." },
];

const COMPLIANCE = [
  { t: "Primary-source verification", d: "RN licensure and national certifications validated against issuing boards before platform activation." },
  { t: "Facility-configurable rule sets", d: "Unit-level requirements for orientation, TB, N95 fit-test, competency checklists, and vaccinations — enforced pre-submission." },
  { t: "Document expiry tracking", d: "Automated alerts on license, BLS, ACLS, PALS, NIHSS, background, and drug screen expirations." },
  { t: "Tamper-evident audit trail", d: "Every credential change, message, and pay event is immutably logged and exportable for Joint Commission surveys." },
  { t: "HIPAA-aware data handling", d: "AES-256 at rest, TLS 1.3 in transit, role-scoped access defaults, SSO and TOTP 2FA available across all roles." },
  { t: "Multi-state payroll compliance", d: "W-2 and 1099 handling across all 50 states, with multi-state tax, benefits, and workers' comp orchestration." },
];

const ENGAGEMENT_STEPS = [
  { s: "01", t: "Discovery", d: "Share your staffing model, rate constraints, facility rules, and program objectives. We respond with a practical scope." },
  { s: "02", t: "MSA & rate card", d: "Procurement-ready master services agreement, rate card, and candidate submission SLAs finalized before kickoff." },
  { s: "03", t: "Facility onboarding", d: "Credential rule sets, scheduler access, billing codes, and reporting cadence configured per unit." },
  { s: "04", t: "Live operations", d: "Requisitions flow into the platform; candidates are submitted pre-cleared; timesheets route for approval weekly." },
  { s: "05", t: "Program review", d: "Monthly metrics review with your CNO, scheduling lead, and finance — adjustments made against agreed targets." },
];

const FAQS = [
  { q: "What's the typical turnaround on a travel requisition?", a: "Qualified shortlists within two business days for standard specialties. ICU, cath lab, and perioperative can require up to five business days depending on licensure and certification requirements." },
  { q: "Do you work with existing MSP / VMS programs?", a: "Yes. TRITAL Care operates as a primary vendor, a subcontracted supplier inside MSP programs, or as the MSP itself. We integrate with existing VMS platforms where required." },
  { q: "How are rates structured?", a: "Transparent rate cards by specialty, shift, and geography with defined bill/pay splits. No undisclosed markups. Discounted rate tiers available for exclusive or volume engagements." },
  { q: "What credentialing standards do you apply?", a: "Primary-source verification on licensure and national certifications, plus facility-specific onboarding checklists (TB, N95, competency, orientation, vaccinations). All documents are tamper-evidently stored and exportable." },
  { q: "Are you HIPAA and SOC 2 aligned?", a: "The platform is HIPAA-aware with AES-256 encryption at rest, TLS 1.3 in transit, role-scoped access, and tamper-evident audit logs. SOC 2 alignment across availability, confidentiality, and processing integrity." },
  { q: "Can you support diverse supplier requirements?", a: "Yes. We report on MBE, WBE, and other diverse supplier spend when configured, and we actively partner with certified diverse staffing partners for MSP tier programs." },
  { q: "What happens if a clinician cancels?", a: "Cancellation is logged and triggers the requisition back into the matching queue. For travel roles we carry a backup-candidate protocol written into the SLA." },
  { q: "Do you payroll 1099 and W-2 clinicians?", a: "Yes. We operate compliant payrolling across all 50 states for W-2, 1099, and employer-of-record arrangements." },
];

export default function Employers() {
  const [faqOpen, setFaqOpen] = useState(null);
  const [form, setForm] = useState({ name: "", title: "", email: "", org: "", need: "Travel staffing", timeline: "Within 30 days", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/request-talent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "employers",
          name: form.name,
          email: form.email,
          title: form.title || null,
          organization: form.org || null,
          need: form.need,
          timeline: form.timeline,
          notes: form.notes || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error("Submission failed");
      toast.success("Request received. A member of our team will respond within one business day.");
      setForm({ name: "", title: "", email: "", org: "", need: "Travel staffing", timeline: "Within 30 days", notes: "" });
    } catch {
      toast.error("We couldn't deliver that message. Please try again or email info@tritalcare.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="employers-page">
      <PageMeta
        title="Healthcare Staffing Solutions for Health Systems | TRITAL Care®"
        description="Travel, PRN, contract, direct-hire, payroll, SOW and EOR/AOR staffing — orchestrated on a VMS-ready, compliance-first platform for enterprise health systems."
        canonical="https://www.tritalcare.com/employers"
      />
      <PublicNav />

      {/* 1. HERO */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Building2 className="w-3.5 h-3.5 text-trital-navy" />
              <span className="overline text-trital-navy">For Health Systems & MSPs</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Staffing capacity, governed by <span className="text-trital-navy">your protocols</span>
              <span className="block text-trital-red">not ours.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-xl leading-relaxed">
              TRITAL Care® is a clinical workforce partner for hospitals, health systems, skilled-nursing operators, and MSP programs. Travel, PRN, local, and permanent placements — delivered against facility-specific compliance rules, procurement-ready terms, and program-level visibility.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="#request" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="employers-hero-request">
                Request Talent <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/contact" className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50">
                Talk to our team
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "<48hr", l: "Typical shortlist turnaround" },
                { k: "50 states", l: "Multi-state payroll coverage" },
                { k: "Primary", l: "Source-verified credentials" },
              ].map(({ k, l }) => (
                <div key={l}>
                  <div className="font-display font-black text-2xl lg:text-3xl text-trital-navy">{k}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-navy relative">
              <img src={heroImg} alt="Healthcare operations leadership" className="w-full h-[480px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-trital-navy-ink/65 via-trital-navy-ink/10 to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 glass-card rounded-2xl p-4">
                <div className="overline text-trital-red">On-platform</div>
                <div className="mt-1 font-display font-bold text-slate-900">Fill rate, overtime exposure, and margin — tracked to the cost center.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. WORKFORCE SOLUTIONS OVERVIEW */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Workforce Solutions</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">One platform. Every staffing obligation.</h2>
          <p className="mt-4 text-slate-600">A consolidated system for marketplace, credentialing, VMS, and compliant payrolling — designed around the workflows procurement, finance, and nursing leadership actually run.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SOLUTIONS.map(({ Icon, t, d }) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all" data-testid={`solution-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center"><Icon className="w-5 h-5" /></div>
              <div className="mt-5 font-display font-bold text-lg">{t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. STAFFING MODELS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Staffing Models</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">Every engagement type your program requires.</h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600">From 13-week travel contracts to MSP orchestration and multi-state payrolling — structured against the terms and reporting procurement expects.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STAFFING_MODELS.map(({ Icon, t, d, anchor }, i) => (
              <motion.div
                key={t}
                id={anchor}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                className="scroll-mt-28 rounded-2xl bg-white border border-slate-200 p-5 shadow-card hover:-translate-y-0.5 hover:shadow-card-hover transition-all"
                data-testid={`model-${anchor}`}
              >
                <div className="w-10 h-10 rounded-xl bg-trital-navy text-white flex items-center justify-center"><Icon className="w-[18px] h-[18px]" /></div>
                <div className="mt-4 font-display font-bold text-[17px] text-slate-900">{t}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. ROLES COVERED */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Roles Covered</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">Clinical coverage across every discipline.</h2>
          <p className="mt-4 text-slate-600">Over 150 specialty and sub-specialty roles — from bedside RN coverage to interim executive leadership.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROLES.map(({ Icon, t, d, chip }) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-0.5 hover:shadow-card-hover transition-all">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-red-50 text-trital-red flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">{chip}</span>
              </div>
              <div className="mt-5 font-display font-bold text-lg">{t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. WHY EMPLOYERS CHOOSE */}
      <section className="bg-trital-navy-ink relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-80 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline text-white/60">Why Employers Choose TRITAL Care®</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-white">Six things operators notice by month two.</h2>
            </div>
            <div className="lg:col-span-5 text-white/70">
              Most programs evaluate us quarterly. The list below is what their scheduling, clinical, and finance teams tend to cite.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map(({ Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
                <div className="w-11 h-11 rounded-xl bg-white/10 text-trital-red flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <div className="mt-5 font-display font-bold text-lg text-white">{t}</div>
                <p className="mt-2 text-[13.5px] text-white/75 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. COMPLIANCE & ONBOARDING DISCIPLINE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline text-trital-red">Compliance & Onboarding</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">Compliance is the prerequisite, not the feature.</h2>
          <p className="mt-4 text-slate-600">Every candidate reaching your scheduler has cleared facility-specific requirements. Every document is primary-source verified. Every action is logged.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["HIPAA-aware", "SOC 2 aligned", "Joint Commission ready", "NCQA friendly"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
          {COMPLIANCE.map(({ t, d }) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-trital-navy" />
                <div className="font-display font-bold text-[15px] text-slate-900">{t}</div>
              </div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7. SUPPLIER DIVERSITY */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="overline text-trital-red">Supplier Diversity</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">Diverse-spend credit, reported to your procurement cadence.</h2>
            <p className="mt-4 text-slate-600">TRITAL Care partners with certified diverse staffing suppliers and can report MBE, WBE, VBE, and LGBTBE spend in your procurement format — quarterly, monthly, or on-demand.</p>
            <ul className="mt-6 space-y-2 text-sm">
              {[
                "Tier-2 diverse spend reporting on every engagement",
                "Partnerships with certified MBE/WBE clinical staffing vendors",
                "Diverse-clinician pipeline programs in partnership with HBCUs and HSIs",
                "Tier-1 diverse candidate tracking when requested by client policy",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-trital-navy mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              { k: "MBE", l: "Minority Business Enterprise partnerships" },
              { k: "WBE", l: "Women Business Enterprise partnerships" },
              { k: "VBE", l: "Veteran Business Enterprise partnerships" },
              { k: "LGBTBE", l: "LGBT Business Enterprise partnerships" },
            ].map(({ k, l }) => (
              <div key={k} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-card">
                <div className="font-display font-black text-3xl text-trital-navy">{k}</div>
                <div className="mt-2 text-[13px] text-slate-600 leading-snug">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. HOW ENGAGEMENT WORKS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">How Engagement Works</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">From discovery call to monthly program review.</h2>
        </div>
        <div className="mt-12 relative">
          <div className="hidden lg:block absolute left-0 right-0 top-[34px] h-px bg-slate-200" />
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {ENGAGEMENT_STEPS.map(({ s, t, d }, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative"
              >
                <div className="relative z-10 w-[70px] h-[70px] rounded-2xl bg-white border border-slate-200 shadow-card flex items-center justify-center mb-4">
                  <div className="font-display font-black text-2xl text-trital-navy">{s}</div>
                </div>
                <div className="font-display font-bold text-lg text-slate-900">{t}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQs */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline text-trital-red">FAQs</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900">Questions procurement asks early.</h2>
          </div>
          <div className="mt-12 space-y-3">
            {FAQS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={f.q} className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid={`faq-${i}`}>
                  <button
                    onClick={() => setFaqOpen(open ? null : i)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                    aria-expanded={open}
                  >
                    <span className="font-display font-bold text-[16px] text-slate-900">{f.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-trital-navy transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
                        <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10. CONTACT / RFP FORM */}
      <section id="request" className="max-w-7xl mx-auto px-5 lg:px-8 py-24 scroll-mt-24">
        <div className="rounded-3xl bg-trital-navy-ink text-white p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-70 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
                <Sparkles className="w-3 h-3 text-trital-red" />
                <span className="overline text-white/80">Start an engagement</span>
              </div>
              <h2 className="mt-5 font-display font-black text-4xl lg:text-5xl tracking-tight text-balance">Request Talent.</h2>
              <p className="mt-4 text-white/80 max-w-md">Share your staffing need. A member of our team responds within one business day with a practical scope, rate framework, and proposed SLA.</p>
              <ul className="mt-6 space-y-2 text-[13.5px] text-white/80">
                {["Procurement-ready MSA and rate card", "Pre-cleared credentials before first submission", "Dedicated executive sponsor on every account"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-trital-red" /> {t}</li>
                ))}
              </ul>
            </div>
            <form onSubmit={submit} className="lg:col-span-7 bg-white text-slate-900 rounded-2xl p-6 lg:p-7 shadow-card-hover" data-testid="employer-rfp-form">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" value={form.name} onChange={upd("name")} required testid="rfp-name" />
                <Field label="Title" value={form.title} onChange={upd("title")} testid="rfp-title" placeholder="e.g., Director, Workforce Strategy" />
                <Field label="Work email" type="email" value={form.email} onChange={upd("email")} required testid="rfp-email" />
                <Field label="Organization" value={form.org} onChange={upd("org")} required testid="rfp-org" />
                <div>
                  <Label>What do you need</Label>
                  <select value={form.need} onChange={upd("need")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="rfp-need">
                    {["Travel staffing", "Local / Per Diem", "Contract staffing", "Direct hire / Permanent", "MSP / VMS program", "SOW / Project-based", "Payroll / EOR", "Other"].map((v) => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Timeline</Label>
                  <select value={form.timeline} onChange={upd("timeline")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="rfp-timeline">
                    {["Within 30 days", "30–60 days", "60–90 days", "90+ days"].map((v) => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>Scope notes (optional)</Label>
                  <textarea rows={4} value={form.notes} onChange={upd("notes")} placeholder="Specialty mix, shift coverage, compliance requirements, MSP context, etc." className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="rfp-notes" />
                </div>
              </div>
              <button type="submit" disabled={submitting} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-60 disabled:cursor-not-allowed" data-testid="rfp-submit">
                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Submit request"}
              </button>
              <div className="mt-3 text-[11px] text-slate-500 text-center">We respond within one business day. No hidden markups or auto-enrollment.</div>
            </form>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA BAND */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="employers-cta-band">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <div>
            <div className="overline text-trital-red">Ready when you are</div>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[52px] leading-[1.05] text-balance">
              Bring your workforce onto one system.
            </h3>
            <p className="mt-4 text-white/80 max-w-[58ch]">
              Procurement-ready paper, pre-cleared credentials, and a single executive sponsor on every account —
              typical response within one business day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <a href="#request" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100" data-testid="employers-final-cta-request">
              Request Talent <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="employers-final-cta-demo">
              Book a demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="/shiftbridge" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" aria-label="PRN Shifts (opens in new tab)" data-testid="employers-final-cta-prn">
              PRN Shifts <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="employers-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.jobs.headline} subtext={SMS_COPY.jobs.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">{children}</label>;
}
function Field({ label, type = "text", value, onChange, required, testid, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid={testid} />
    </div>
  );
}
